<script setup lang="ts">
import { useDivisibleReceiver } from '@precon-tools-suite-by-shared/vue-divisibles';
import { usePartialComponent } from '@precon-tools-suite-by-shared/vue-partials';
import { type Component, ref } from 'vue';
import Skeleton from 'primevue/skeleton';
import Button from 'primevue/button';

const props = withDefaults(
    defineProps<{
        partial: Component | string;
        fallbackPartial?: Component | string | null;
        partialProps?: Record<string, unknown>;
        collapsable?: boolean;
    }>(),
    {
        fallbackPartial: null,
        partialProps: () => ({}),
        collapsable: false,
    }
);

const receiver = useDivisibleReceiver();
const partialComponent = usePartialComponent(() => props.partial, {
    fallback: () => props.fallbackPartial,
});

const { component: HeaderPart, exists: headerExists } = receiver.use('header');
const { component: TitlePart, exists: titleExists } = receiver.use('title');
const { component: ToolsPart, exists: toolsExists } = receiver.use('tools');

// COLLAPSE/EXPAND

const collapsed = ref<boolean>(true);

function toggleCollapsed() {
    if (props.collapsable) {
        collapsed.value = !collapsed.value;
    }
}
</script>

<template>
    <section
        class="rightpanel-section"
        :class="{ 'rightpanel-section-collapsable': collapsable }"
    >
        <div class="flex section-header">
            <Button
                v-if="collapsable"
                class="flex-shrink-1 p-button-rounded p-button-text p-button-secondary"
                :icon="collapsed ? 'pi pi-angle-right' : 'pi pi-angle-down'"
                @click="toggleCollapsed"
            />
            <HeaderPart v-if="headerExists" />
            <h6 v-else class="mr-auto" @click="toggleCollapsed">
                <TitlePart v-if="titleExists">
                    <Skeleton style="width: 6rem" />
                </TitlePart>
            </h6>
            <ToolsPart v-if="toolsExists">
                <Button
                    class="p-button-rounded p-button-text p-button-secondary p-panel-header-icon"
                    icon="pi pi-spinner pi-spin"
                    disabled
                />
            </ToolsPart>
        </div>

        <div v-show="!collapsed" class="section-content">
            <component
                :is="partialComponent"
                v-bind="partialProps"
                :collapsed="collapsed"
            >
                <slot />
            </component>
        </div>
    </section>
</template>

<style scoped lang="scss">
.section-header {
    justify-content: left !important;
    align-items: center;
}

.rightpanel-section-collapsable {
    .section-header h6 {
        cursor: pointer !important;
    }
}
</style>
